import {useCallback, useState, type FormEvent} from 'react'
import {Link, Navigate, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Hint} from '@/components/ui/hint'
import {Label} from '@/components/ui/label'
import {FieldError} from '@/components/field-error'
import {AuthShell} from '@/components/auth-shell'
import {GoogleSignInButton} from '@/components/google-signin-button'
import {useAuth} from '@/hooks/platform/use-auth'
import {api} from '@/lib/api'
import {translateApiError} from '@/lib/errors'
import {validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'
import {rutaDeRetorno} from '@/lib/retorno-login'

type LoginFieldErrors = {cedula?: string; password?: string}

export function LoginPage() {
  const {t} = useTranslation()
  const {user, signIn, completeAuth} = useAuth()
  const [params] = useSearchParams()
  // KAI-353: la misma vuelta para los dos caminos (contraseña y Google), que comparten este render.
  const [destino] = useState(() => rutaDeRetorno(params.toString()))
  const [cedula, setCedula] = useState('')
  const [password, setPassword] = useState('')
  const [fieldErrors, setFieldErrors] = useState<LoginFieldErrors>({})
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // El backend verifica el ID token de Google y solo empareja una persona existente; nunca crea cuentas.
  const handleGoogle = useCallback(
    async (credential: string) => {
      setError(null)
      try {
        const {data} = await api.post('/auth/google', {credential})
        await completeAuth(data.access_token)
      } catch (err) {
        setError(translateApiError(err))
      }
    },
    [completeAuth]
  )

  if (user) return <Navigate to={destino} replace />

  function blur(field: keyof LoginFieldErrors, value: string) {
    setFieldErrors((fe) => ({...fe, [field]: validateRequired(value) ?? undefined}))
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const errors: LoginFieldErrors = {
      cedula: validateRequired(cedula) ?? undefined,
      password: validateRequired(password) ?? undefined,
    }
    setFieldErrors(errors)
    if (errors.cedula || errors.password) return
    setSubmitting(true)

    const {error} = await signIn(cedula, password)

    setSubmitting(false)
    if (error) setError(error)
  }

  return (
    <AuthShell>
      <h1 className="font-heading text-3xl tracking-tight text-foreground">{t('auth.signIn')}</h1>

      <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-5">
        <div className="space-y-2">
          <Label htmlFor="cedula">{t('auth.cedula')}</Label>
          <Input
            // DOC (20) y no CEDULA (10): LoginRequest.cedula no tiene tope y por ahí entran pasaporte,
            // PEP (15) y PPT; cortar en 10 le impediría a esa persona teclear su documento.
            maxLength={tope('DOC')}
            id="cedula"
            value={cedula}
            onChange={(e) => {
              setCedula(e.target.value)
              setFieldErrors((fe) => ({...fe, cedula: undefined}))
            }}
            onBlur={() => blur('cedula', cedula)}
            aria-invalid={!!fieldErrors.cedula}
            required
          />
          <FieldError error={fieldErrors.cedula} />
          <Hint>{t('auth.cedulaHint')}</Hint>
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">{t('auth.password')}</Label>
          <Input
            maxLength={tope('PASS')}
            id="password"
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
              setFieldErrors((fe) => ({...fe, password: undefined}))
            }}
            onBlur={() => blur('password', password)}
            aria-invalid={!!fieldErrors.password}
            required
            minLength={6}
          />
          <FieldError error={fieldErrors.password} />
        </div>
        {error && (
          <div className="notice-red space-y-1 rounded-lg px-3.5 py-3">
            <p className="text-sm font-medium">{error}</p>
            <p className="text-xs opacity-80">{t('auth.firstAccessHint')}</p>
          </div>
        )}
        <Button type="submit" className="w-full" disabled={submitting}>
          {t('auth.signIn')}
        </Button>
        <Link
          to="/reset-request"
          className="block text-center text-sm text-primary hover:underline"
          data-testid="forgot-password">
          {t('auth.forgotPassword')}
        </Link>
      </form>

      <div className="mt-6">
        <GoogleSignInButton onCredential={handleGoogle} />
      </div>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        {t('auth.noAccount')}{' '}
        <Link
          to="/registro/empresa"
          className="font-medium text-primary hover:underline"
          data-testid="registro-empresa-link">
          {t('auth.registerCompany')}
        </Link>
      </p>
    </AuthShell>
  )
}
